import qs from 'qs'
import { authStore } from "~/stores/auth";
import { computed, useState } from '#imports';
import {useUserStore} from "~/stores/userStore";
import { missionStore as useMissionStore } from '~/stores/mission';

export const useAuth = () => {
    const router = useRouter();
    const companyStore = useCompanyStore();

    // The single source of truth for authentication state.
    const status = useState<'loading' | 'authenticated' | 'unauthenticated'>('auth:status', () => 'loading');

    // A computed property for convenience and backward compatibility.
    const isAuthenticated = computed(() => status.value === 'authenticated');

    const checkAuth = async () => {

        // Check if we are on the subscribe page
        const route = useRoute();
        const isOnSubscribePage = route.path.startsWith('/subscribe');

        status.value = 'loading';
        const { $api } = useNuxtApp();

        try {
            const response = await $api().v2.auth.me();

            if (response.id) {
                // If auth validation succeeds, set user and status.
                authStore().setUser(response);
                status.value = 'authenticated';

                // If we're on the subscription page, stop here
                if (isOnSubscribePage) {
                    return true;
                }

                // Fetch all companies for the user
                try {
                    await companyStore.fetchCompanies();
                } catch (error) {
                    console.error('Error fetching companies:', error);
                }

                return true;
            } else {
                status.value = 'unauthenticated';
                return false;
            }
        } catch (error) {
            console.error('Error checking authentication:', error);
            status.value = 'unauthenticated';
            return false;
        }
    };


    const loginUser = async (credentials: { email: string; password: string }) => {
        status.value = 'loading';

        const formData = qs.stringify({
            email: credentials.email,
            password: credentials.password,
        });

        const { $api } = useNuxtApp();
        try {
            await $api().v1.auth.login(formData);
            await checkAuth();
            if (isAuthenticated.value) {
                // Fetch companies and set currentCompany cookie after successful login
                const response = await companyStore.fetchCompanies();
                if (response.companies.length > 0) {
                    useCookie('currentCompany').value = response.companies[0].company_id;
                }
                router.push('/dashboard');
            } else {
                console.log('Login process finished, but user is not authenticated.');
            }
        } catch (error) {
            console.error('Login failed:', error);
            status.value = 'unauthenticated'; // Explicitly set to unauthenticated on login failure
        }
    };

    const logoutUser = async () => {
        status.value = 'loading';
        const { $api } = useNuxtApp();
        try {
            // Clear mission draft before resetting auth (needs user ID for scoped key)
            useMissionStore().clearDraft();

            // Clear dismissed alerts so banner reappears on next login
            if (import.meta.client) {
                sessionStorage.removeItem('alerts_dismissed');
            }

            await $api().v2.auth.logout();
            authStore().$reset();
            useUserStore().logout();
            companyStore.$reset();
            useCookie('currentCompany').value = null;

            // Redirect to login page
            router.push('/login');
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            // No matter what, user is unauthenticated after a logout attempt.
            status.value = 'unauthenticated';
        }
    };

    return {
        isAuthenticated, // The computed boolean
        status,         // The reactive status string ('loading', 'authenticated', 'unauthenticated')
        loginUser,
        logoutUser,
        checkAuth,
    };
};